import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import FinancialDashboard from '../components/FinancialDashboard'
import LanguageToggle from '../components/LanguageToggle'
import UserEmailBadge from '../components/UserEmailBadge'

const CustomerDashboard = () => {
  const { user, signOut } = useAuth()
  const { t } = useTranslation(['dashboard', 'common'])
  const navigate = useNavigate()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [dataVersion, setDataVersion] = useState(0)

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) {
      console.error('Error signing out:', error)
    }
    navigate('/login')
  }

  const handleOpenDeleteModal = () => {
    setDeleteError('')
    setShowDeleteModal(true)
  }

  const handleCancelDelete = () => {
    if (isDeleting) return
    setShowDeleteModal(false)
    setDeleteError('')
  }

  const handleConfirmDelete = async () => {
    if (!user || isDeleting) return

    setIsDeleting(true)
    setDeleteError('')

    try {
      const { error } = await supabase.rpc('delete_business_metrics_and_automation')

      if (error) {
        throw error
      }

      setShowDeleteModal(false)
      setDataVersion((v) => v + 1)
    } catch (err) {
      console.error('Error deleting business data:', err)
      setDeleteError(t('customer.deleteDataError'))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold text-gray-900">
                <span className="text-[#FF1B54]">Interactive </span>
                <span className="text-gray-900">Business</span>
              </h1>
              <div className="flex items-center space-x-4">
                <LanguageToggle />
                <UserEmailBadge email={user?.email} showProfileMenu />
                <button
                  onClick={handleLogout}
                  className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  {t('navigation.logout', { ns: 'common' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Financial Dashboard Component */}
          <FinancialDashboard
            key={dataVersion}
            onDeleteData={handleOpenDeleteModal}
          />
        </div>
      </main>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('customer.deleteDataConfirmTitle')}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              {t('customer.deleteDataConfirmBody')}
            </p>
            {deleteError && (
              <div className="mb-3 text-sm text-red-600">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end space-x-3 mt-2">
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('customer.deleteDataConfirmNo')}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#FF1B54] border border-transparent rounded-md hover:bg-[#E1174C] disabled:opacity-50"
              >
                  {isDeleting ? t('customer.deletingData') : t('customer.deleteDataConfirmYes')}
                </button>
              </div>
            </div>
        </div>
      )}
    </div>
  )
}

export default CustomerDashboard
